"use client";

import {AnimatePresence, motion} from "framer-motion";
import {usePathname, useRouter} from "@/i18n/navigation";
import {useLocale, useTranslations} from "next-intl";
import {useEffect, useState, useTransition} from "react";

type Locale = "en" | "hi";

const STORAGE_KEY = "blooming-dental-language-selected";

export function LanguageModal() {
  const t = useTranslations("language");
  const locale = useLocale() as Locale;
  const pathname = usePathname();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    const hasVisited = window.localStorage.getItem(STORAGE_KEY);

    if (!hasVisited) {
      const timer = window.setTimeout(() => {
        setIsOpen(true);
      }, 0);

      return () => window.clearTimeout(timer);
    }

    return undefined;
  }, []);

  const handleSelect = (nextLocale: Locale) => {
    window.localStorage.setItem(STORAGE_KEY, "1");
    setIsOpen(false);

    if (nextLocale === locale) return;

    startTransition(() => {
      router.replace(pathname, {locale: nextLocale});
    });
  };

  return (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          className="fixed inset-0 z-50 grid place-items-center bg-[#0b201d]/75 p-4 backdrop-blur-sm"
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          exit={{opacity: 0}}
          aria-hidden={false}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="language-dialog-title"
            className="w-full max-w-xl rounded-[2rem] bg-[#fffefa] p-6 shadow-[0_28px_90px_-30px_rgba(0,0,0,0.45)] ring-1 ring-white/40 md:p-8 dark:bg-[#17302c] dark:ring-white/10"
            initial={{opacity: 0, y: 20, scale: 0.98}}
            animate={{opacity: 1, y: 0, scale: 1}}
            exit={{opacity: 0, y: 8, scale: 0.98}}
            transition={{duration: 0.2, ease: "easeOut"}}
          >
            <h2
              id="language-dialog-title"
              className="clinic-display text-center text-3xl font-semibold text-[var(--clinic-ink)] md:text-4xl"
            >
              {t("chooseTitle")}
            </h2>
            <p className="mt-2 text-center text-lg font-medium text-[var(--clinic-muted)]">
              {t("chooseSubtitle")}
            </p>
            <div className="mt-8 grid gap-4 md:grid-cols-2">
              <button
                type="button"
                onClick={() => handleSelect("en")}
                disabled={isPending}
                className="min-h-28 rounded-2xl bg-[var(--clinic-brand)] px-6 py-8 text-2xl font-bold text-white transition hover:-translate-y-0.5 hover:bg-[var(--clinic-brand-hover)] disabled:opacity-60"
                aria-label={t("switchToEnglish")}
              >
                ENGLISH
              </button>
              <button
                type="button"
                onClick={() => handleSelect("hi")}
                disabled={isPending}
                className="min-h-28 rounded-2xl bg-[var(--clinic-accent-fill)] px-6 py-8 text-2xl font-bold text-white transition hover:-translate-y-0.5 hover:bg-[var(--clinic-accent-fill-hover)] disabled:opacity-60"
                aria-label={t("switchToHindi")}
              >
                हिंदी
              </button>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
